import { useEffect, useRef, useState, type CSSProperties } from "react";
import BarcodeScannerModal from "../common/BarcodeScannerModal";
import DenominationCounter from "./DenominationCounter";
import { anyCounted, denomTotalCents, nonZeroDenoms, type DenomCounts } from "../../lib/denominations";
import {
  reopenDrawerDeposit,
  verifyDrawerDeposit,
  type CashDrawerCountRow,
  type CashDrawerShift,
} from "../../lib/api";
import {
  bagExpectedCents,
  bagMatch,
  buildVerifyNote,
  checkBagVerification,
  closingCountForShift,
  type HeldOverDay,
  dayBagStatus,
  isZeroBag,
  parseDollarsToCents,
} from "../../lib/depositVerify";

interface Props {
  shifts: CashDrawerShift[];
  counts: CashDrawerCountRow[];
  /** owner / manager: may verify bags. The RPC enforces the same rule. */
  canVerify: boolean;
  /** owner: may re-open a verified bag. */
  canReopen: boolean;
  deviceLabel: (name: string) => string;
  /** Later days' cash on drawers left open for days, by shift id (splitHeldOverCash). */
  heldOver?: Record<string, HeldOverDay[]>;
  onChanged: () => Promise<void> | void;
  /** After a person's verify / recount / re-open succeeds (not auto-verify). */
  afterUserAction?: () => void;
  notify: (msg: string) => void;
}

interface BagForm {
  counted: string;
  bag: string;
  note: string;
  error: string | null;
  /** Set while counting by denomination; the counter's total is then the count. */
  denoms: DenomCounts | null;
}

const EMPTY_FORM: BagForm = { counted: "", bag: "", note: "", error: null, denoms: null };

/** The amount being verified, as typed or as the denomination counter's total. */
const countedText = (f: BagForm) => (f.denoms ? (denomTotalCents(f.denoms) / 100).toFixed(2) : f.counted);

const fmt = (cents: number | null | undefined) =>
  "$" + ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtShortDay = (iso: string | null | undefined) =>
  iso ? new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
const fmtSigned = (cents: number) => (cents > 0 ? "+" : cents < 0 ? "−" : "") + fmt(Math.abs(cents));

/**
 * Manager/owner verification of each deposit bag against Square cash sales.
 * One bag per CLOSED Square drawer shift; it does not need the closer's count.
 * Works on any day, unlike counts, which only file under the current business day.
 */
export default function DepositBagsSection({
  shifts,
  counts,
  canVerify,
  canReopen,
  deviceLabel,
  heldOver = {},
  onChanged,
  afterUserAction,
  notify,
}: Props) {
  const [forms, setForms] = useState<Record<string, BagForm>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [scanFor, setScanFor] = useState<string | null>(null);
  const amountRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const status = dayBagStatus(shifts);
  const attempted = useRef<Set<string>>(new Set());
  // Refusals keyed by shift id, so a message only shows for shifts on screen.
  const [autoFailed, setAutoFailed] = useState<Record<string, string>>({});
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true; // StrictMode re-runs effects after a simulated unmount
    return () => {
      mounted.current = false;
    };
  }, []);

  // Registers with no cash sales (and nothing paid in or bagged by the closer)
  // are verified at $0 automatically, as the back office did from manager
  // devices. Each shift is tried once per page; a refusal is kept and that
  // shift gets a normal card. Results are applied even if the parent re-renders
  // mid-batch (it passes a new shifts array on every render).
  useEffect(() => {
    if (!canVerify) return;
    const todo = shifts.filter((sh) => isZeroBag(sh, counts, shifts) && !attempted.current.has(sh.id));
    if (todo.length === 0) return;
    todo.forEach((sh) => attempted.current.add(sh.id));
    (async () => {
      let done = 0;
      const failed: Record<string, string> = {};
      for (const sh of todo) {
        try {
          await verifyDrawerDeposit({ shift_id: sh.id, counted_cents: 0, notes: "Auto-verified: no cash sales in Square" });
          done++;
        } catch (err: any) {
          failed[sh.id] = err?.message || "failed";
        }
      }
      if (!mounted.current) return;
      if (Object.keys(failed).length) setAutoFailed((prev) => ({ ...prev, ...failed }));
      if (done > 0) await onChanged();
    })();
  }, [shifts, counts, canVerify]); // eslint-disable-line react-hooks/exhaustive-deps

  if (shifts.length === 0) return null;

  const formFor = (id: string) => forms[id] ?? EMPTY_FORM;
  const patch = (id: string, p: Partial<BagForm>) =>
    setForms((f) => ({ ...f, [id]: { ...(f[id] ?? EMPTY_FORM), ...p } }));

  // Being auto-verified: no card. A refused one gets a normal card so a person can act.
  const zeroShifts = shifts.filter((s) => isZeroBag(s, counts, shifts) && !autoFailed[s.id]);
  const cashShifts = shifts.filter((s) => !(isZeroBag(s, counts, shifts) && !autoFailed[s.id]));
  const failuresHere = shifts.filter((s) => autoFailed[s.id] && !s.staff_counted_at);

  const handleVerify = async (shift: CashDrawerShift) => {
    const f = formFor(shift.id);
    const closer = closingCountForShift(shift, counts, shifts);
    const match = bagMatch(f.bag, closer?.deposit_bag_number);
    const expected = bagExpectedCents(shift);
    const check = checkBagVerification({ expectedCents: expected, countedInput: countedText(f), note: f.note, match });
    if (!check.ok) {
      patch(shift.id, { error: check.error ?? "Check the entry." });
      return;
    }
    setBusy(shift.id);
    try {
      const res = await verifyDrawerDeposit({
        shift_id: shift.id,
        counted_cents: parseDollarsToCents(countedText(f))!,
        denominations: f.denoms && anyCounted(f.denoms) ? nonZeroDenoms(f.denoms) : undefined,
        notes: buildVerifyNote(f.bag, match, f.note),
      });
      setForms((all) => {
        const next = { ...all };
        delete next[shift.id];
        return next;
      });
      notify(
        `✓ ${deviceLabel(shift.device_name)} bag verified by ${res.counted_by}.` +
          (res.day_complete ? ` Day complete: ${fmt(res.day_total_cents)} ready for the bank run.` : "")
      );
      await onChanged();
      afterUserAction?.();
    } catch (err: any) {
      patch(shift.id, { error: err?.message || "Verification failed." });
    } finally {
      setBusy(null);
    }
  };

  const handleReopen = async (shift: CashDrawerShift) => {
    const label = deviceLabel(shift.device_name);
    const zeroBag = bagExpectedCents(shift) === 0;
    const reason = window.prompt(
      zeroBag
        ? `Recount the $0 bag for ${label}? The old count is kept in the audit log and the new one replaces it.\n\nReason (required):`
        : `Re-open the verified bag for ${label}? The old count is kept in the audit log, the day becomes unverified, and a bank run that covered it is flagged stale.\n\nReason (required):`
    );
    if (reason == null) return;
    if (!reason.trim()) {
      alert("A reason is required to re-open a bag.");
      return;
    }
    // A no-sales bag is re-counted in the same step: left unverified it would be
    // auto-verified at $0 again by the next manager who opens this day.
    let recountCents: number | null = null;
    if (zeroBag) {
      const amount = window.prompt(`Amount actually in the ${label} bag ($):`, "0.00");
      if (amount == null) return;
      recountCents = parseDollarsToCents(amount);
      if (recountCents == null) {
        alert("Enter the amount in the bag, e.g. 0 or 12.50.");
        return;
      }
    }
    setBusy(shift.id);
    try {
      const res = await reopenDrawerDeposit({ shift_id: shift.id, reason: reason.trim() });
      let recountFailed = false;
      if (recountCents != null) {
        try {
          await verifyDrawerDeposit({
            shift_id: shift.id,
            counted_cents: recountCents,
            notes: `Recounted after re-open: ${reason.trim()}`,
          });
        } catch (err: any) {
          // Keep it on a card and out of auto-verify, so the owner's amount is not replaced by $0.
          recountFailed = true;
          attempted.current.add(shift.id);
          setAutoFailed((prev) => ({ ...prev, [shift.id]: "recount not saved" }));
          alert(
            `The bag was re-opened but the recount was not saved (${err?.message || "failed"}). ` +
              "Enter the count on its card below."
          );
        }
      }
      notify(
        (recountCents != null && !recountFailed
          ? `${label} bag recounted at ${fmt(recountCents)}.`
          : `Bag re-opened for ${label}.`) +
          (res.staled_run_id ? " The bank run that covered this day is now flagged stale." : "")
      );
      await onChanged();
      // Stay put when the recount failed: the alert points at this bag's card.
      if (!recountFailed) afterUserAction?.();
    } catch (err: any) {
      alert("Re-open failed: " + (err?.message || "failed"));
    } finally {
      setBusy(null);
    }
  };

  const summary =
    status.closed === 0
      ? "No drawers closed in Square yet."
      : status.pending === 0
      ? `All ${status.closed} bag(s) verified.`
      : `${status.verified} of ${status.closed} bag(s) verified.`;

  return (
    <div style={s.wrap}>
      <div style={s.heading}>
        <span>Deposit Bags</span>
        <span style={status.pending === 0 && status.closed > 0 ? s.okText : s.mutedText}>{summary}</span>
      </div>
      {status.notClosed > 0 && (
        <div style={s.warnLine}>
          ⚠ {status.notClosed} drawer(s) not closed in Square. Close them in Square first; the day cannot be verified until then.
        </div>
      )}
      {!canVerify && status.pending > 0 && (
        <div style={s.mutedLine}>Awaiting manager or owner verification.</div>
      )}

      {canVerify && zeroShifts.length > 0 && (
        <div style={s.mutedLine}>
          Verifying {zeroShifts.length} no-cash drawer(s) at $0 automatically:{" "}
          {zeroShifts.map((z) => deviceLabel(z.device_name)).join(", ")}…
        </div>
      )}
      {failuresHere.length > 0 && (
        <div style={s.warnLine}>
          Could not auto-verify{" "}
          {failuresHere.map((f) => `${deviceLabel(f.device_name)} (${autoFailed[f.id]})`).join("; ")}. Count it on its card below.
        </div>
      )}

      <div style={s.list}>
        {cashShifts.map((shift) => {
          const expected = bagExpectedCents(shift);
          const closer = closingCountForShift(shift, counts, shifts);
          const recordedBag = closer?.deposit_bag_number || null;
          const f = formFor(shift.id);
          const match = bagMatch(f.bag, recordedBag);
          const counted = parseDollarsToCents(countedText(f));
          // While counting by denomination, no variance until something is counted.
          const variance = counted == null || (f.denoms && !anyCounted(f.denoms)) ? null : counted - expected;
          const verified = !!shift.staff_counted_at;
          const closed = shift.state === "CLOSED";

          return (
            <div key={shift.id} style={f.denoms ? { ...s.card, ...s.cardWide } : s.card}>
              <div style={s.cardHead}>
                <strong>{deviceLabel(shift.device_name)}</strong>
                <span style={verified ? s.okText : closed ? s.warnText : s.mutedText}>
                  {verified ? "✓ Verified" : closed ? "Needs verification" : `Drawer ${shift.state.toLowerCase()} in Square`}
                </span>
              </div>
              <div style={s.row}>
                <span>Square cash sales (expected)</span>
                <strong>{fmt(expected)}</strong>
              </div>
              {heldOver[shift.id] && (
                <div style={s.heldOver}>
                  ⚠ This drawer was left open in Square from {fmtShortDay(shift.opened_at)} to{" "}
                  {fmtShortDay(shift.closed_at)}. Only cash taken on this day is expected in this bag. Taken later:{" "}
                  {heldOver[shift.id].map((d) => `${fmtShortDay(d.business_date)} ${fmt(d.cents)}`).join(" · ")}.
                </div>
              )}
              {(shift.opening_staff_name?.trim() || shift.closing_staff_name?.trim()) && (
                <div style={s.row}>
                  <span>Square drawer</span>
                  <span>
                    opened by {shift.opening_staff_name?.trim() || "—"}
                    {shift.closing_staff_name?.trim() ? ` · closed by ${shift.closing_staff_name.trim()}` : ""}
                  </span>
                </div>
              )}
              <div style={s.row}>
                <span>Closer</span>
                <span>
                  {closer
                    ? `${closer.counted_by_name || "Counted"} · bag ${recordedBag ? "#" + recordedBag : "no number"}${
                        closer.deposit_cents != null ? ` · ${fmt(closer.deposit_cents)}` : ""
                      }`
                    : "No employee closing count"}
                </span>
              </div>

              {verified && (
                <>
                  <div style={s.row}>
                    <span>Counted by {shift.staff_counted_by_name || "—"}</span>
                    <strong>
                      {fmt(shift.staff_counted_cents)}{" "}
                      <span style={(shift.staff_counted_cents ?? 0) - expected === 0 ? s.okText : s.badText}>
                        ({fmtSigned((shift.staff_counted_cents ?? 0) - expected)})
                      </span>
                    </strong>
                  </div>
                  {shift.staff_count_notes && <div style={s.note}>{shift.staff_count_notes}</div>}
                  {canReopen && (
                    <button type="button" style={s.linkBtn} disabled={busy !== null} onClick={() => handleReopen(shift)}>
                      {bagExpectedCents(shift) === 0 ? "Recount bag (owner)" : "Re-open bag (owner)"}
                    </button>
                  )}
                </>
              )}

              {!verified && closed && canVerify && (
                <div style={s.form}>
                  <label style={s.label}>Bag number (scan or type)</label>
                  <div style={s.inline}>
                    <input
                      style={s.input}
                      value={f.bag}
                      placeholder={recordedBag ? `Closer recorded #${recordedBag}` : "Scan the bag barcode"}
                      onChange={(e) => patch(shift.id, { bag: e.target.value, error: null })}
                      onKeyDown={(e) => {
                        // Handheld scanners type the code then press Enter.
                        if (e.key === "Enter") {
                          e.preventDefault();
                          amountRefs.current[shift.id]?.focus();
                        }
                      }}
                    />
                    <button type="button" style={s.btnSmall} onClick={() => setScanFor(shift.id)} aria-label="Scan bag barcode with camera">
                      📷 Scan
                    </button>
                  </div>
                  {match === "match" && <div style={s.okText}>✓ Matches the closer's bag</div>}
                  {match === "mismatch" && <div style={s.badText}>⚠ Closer recorded #{recordedBag}. Add a note.</div>}

                  <div style={s.labelRow}>
                    <label style={s.label}>Counted in bag ($)</label>
                    <button
                      type="button"
                      style={s.linkBtn}
                      onClick={() =>
                        // Switching back to typing keeps the counted total rather than discarding it.
                        f.denoms
                          ? patch(shift.id, { denoms: null, counted: anyCounted(f.denoms) ? countedText(f) : f.counted, error: null })
                          : patch(shift.id, { denoms: {}, error: null })
                      }
                    >
                      {f.denoms ? "Type a total instead" : "🧮 Count by denomination"}
                    </button>
                  </div>
                  {f.denoms ? (
                    <>
                      <DenominationCounter
                        value={f.denoms}
                        onChange={(next) => patch(shift.id, { denoms: next, error: null })}
                        compact
                      />
                      <div style={s.totalRow}>
                        <span>Total counted</span>
                        <strong>{fmt(counted ?? 0)}</strong>
                      </div>
                    </>
                  ) : (
                    <input
                      ref={(el) => {
                        amountRefs.current[shift.id] = el;
                      }}
                      style={s.input}
                      inputMode="decimal"
                      placeholder={fmt(expected)}
                      value={f.counted}
                      onChange={(e) => patch(shift.id, { counted: e.target.value, error: null })}
                    />
                  )}
                  {variance != null && (
                    <div style={variance === 0 ? s.okText : s.badText}>
                      {variance === 0 ? "✓ Matches cash sales" : `Variance ${fmtSigned(variance)}. A note is required.`}
                    </div>
                  )}

                  <label style={s.label}>Note</label>
                  <input
                    style={s.input}
                    value={f.note}
                    placeholder="Required for any variance or bag mismatch"
                    onChange={(e) => patch(shift.id, { note: e.target.value, error: null })}
                  />
                  {f.error && <div style={s.badText}>{f.error}</div>}
                  <button type="button" style={s.goldBtn} disabled={busy !== null} onClick={() => handleVerify(shift)}>
                    {busy === shift.id ? "Verifying…" : "Verify bag"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <BarcodeScannerModal
        isOpen={scanFor !== null}
        title="Scan Deposit Bag Barcode"
        lockOrientation={false}
        validateRetailChecksum={false}
        manualLabel="Enter bag number"
        manualPlaceholder="Bag number"
        onClose={() => setScanFor(null)}
        onScan={(code) => {
          if (scanFor) {
            patch(scanFor, { bag: code.trim(), error: null });
            const id = scanFor;
            setTimeout(() => amountRefs.current[id]?.focus(), 0);
          }
          setScanFor(null);
        }}
      />
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  wrap: { marginTop: 20 },
  heading: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: 8,
    fontSize: 14,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 10,
  },
  list: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 },
  card: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    minWidth: 0,
  },
  // A card being counted by denomination takes the full row so the steppers fit.
  cardWide: { gridColumn: "1 / -1", maxWidth: 560 },
  labelRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid rgba(249, 175, 54, 0.4)",
    color: "var(--gold, #F9AF36)",
    fontSize: 15,
  },
  heldOver: {
    fontSize: 12,
    lineHeight: 1.4,
    color: "var(--gold, #F9AF36)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    borderRadius: 6,
    padding: "6px 8px",
  },
  cardHead: { display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14 },
  row: { display: "flex", justifyContent: "space-between", gap: 8, overflowWrap: "anywhere" },
  note: { fontSize: 12, color: "var(--muted, #8A7A66)", overflowWrap: "anywhere" },
  form: { display: "flex", flexDirection: "column", gap: 6, marginTop: 6, paddingTop: 8, borderTop: "1px dashed var(--border, #2A1F14)" },
  label: { fontSize: 11, color: "var(--muted, #8A7A66)", textTransform: "uppercase", letterSpacing: "0.04em" },
  inline: { display: "flex", gap: 8 },
  input: {
    flex: 1,
    minWidth: 0,
    background: "rgba(0,0,0,0.3)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    color: "var(--text, #F0E6D4)",
    padding: "10px 12px",
    fontSize: 16,
    minHeight: 46,
  },
  btn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 46,
  },
  btnSmall: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 8,
    padding: "0 12px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 46,
    whiteSpace: "nowrap",
  },
  goldBtn: {
    background: "var(--gold, #F9AF36)",
    border: "none",
    color: "#111",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 46,
    marginTop: 4,
  },
  linkBtn: {
    alignSelf: "flex-start",
    background: "transparent",
    border: "none",
    color: "var(--muted, #8A7A66)",
    textDecoration: "underline",
    fontSize: 12,
    cursor: "pointer",
    padding: "6px 0",
    minHeight: 32,
  },
  warnLine: { fontSize: 13, color: "var(--gold, #F9AF36)", marginBottom: 10 },
  mutedLine: { fontSize: 13, color: "var(--muted, #8A7A66)", marginBottom: 10 },
  okText: { color: "var(--green, #27ae60)", fontSize: 12, fontWeight: 700 },
  warnText: { color: "var(--gold, #F9AF36)", fontSize: 12, fontWeight: 700 },
  badText: { color: "var(--red, #E05040)", fontSize: 12, fontWeight: 700 },
  mutedText: { color: "var(--muted, #8A7A66)", fontSize: 12, fontWeight: 600 },
};
